import { auth } from '../firebase';
import { normalizeVia, viaFor, withVia } from '../config/referralVia';

const KEY = 'cfa_referral_v1';
const CODE = /^[A-F0-9]{16}$/;
const HANDLE = /^[a-z0-9][a-z0-9_-]{1,18}[a-z0-9]$/;
/** Generated codes are stored uppercase, chosen usernames lowercase; the server resolves both. */
const normalizeRef = raw => {
  const value = String(raw || '').trim();
  if (CODE.test(value.toUpperCase())) return value.toUpperCase();
  return HANDLE.test(value.toLowerCase()) ? value.toLowerCase() : '';
};
const TTL = 30 * 86400000;

/** Valid referral on this URL, independent of retained first-touch attribution. */
export function referralFromSearch(search) {
  return normalizeRef(new URLSearchParams(search).get('ref'));
}

export function captureReferral() {
  try {
    const params = new URLSearchParams(window.location.search);
    const code = referralFromSearch(window.location.search);
    if (!code) return getReferralCode();
    const old = JSON.parse(window.localStorage.getItem(KEY) || 'null');
    if (!old || old.expiresAt < Date.now()) {
      // First touch wins: the code and its share channel are kept together for 30 days.
      window.localStorage.setItem(KEY, JSON.stringify({ code, via: normalizeVia(params.get('via')), expiresAt: Date.now() + TTL }));
    }
  } catch { /* storage may be unavailable */ }
  return getReferralCode();
}

/** Share channel of the stored referral ('direct' when the link had no tag, or none is stored). */
export function getReferralVia() {
  try {
    const row = JSON.parse(window.localStorage.getItem(KEY) || 'null');
    return row?.expiresAt > Date.now() ? normalizeVia(row.via) : 'direct';
  } catch { return 'direct'; }
}

/** The /r/ link tagged with the share channel it is sent through (e.g. ?via=wa). */
export function taggedLink(link, channel, source) {
  return withVia(link, viaFor(channel, source));
}

export function getReferralCode() {
  try {
    const row = JSON.parse(window.localStorage.getItem(KEY) || 'null');
    return row?.expiresAt > Date.now() && normalizeRef(row.code) === row.code ? row.code : '';
  } catch { return ''; }
}

export async function referralRequest(action, fields = {}) {
  const headers = { 'Content-Type': 'application/json' };
  if (!['config', 'visit'].includes(action)) {
    if (!auth.currentUser) throw new Error('Sign in to use referrals.');
    headers.Authorization = `Bearer ${await auth.currentUser.getIdToken()}`;
  }
  const res = await fetch('/.netlify/functions/referrals', {
    method: 'POST', headers, body: JSON.stringify({ action, ...fields }),
  });
  const body = await res.json();
  if (!res.ok) throw new Error(body.error || 'Referrals are unavailable. Please retry.');
  return body;
}

/** A reward past its 14-day hold that still waits for the referrer's credit-or-cash choice. */
export function canChooseReward(row, now = Date.now()) {
  return row?.status === 'pending' && !row.rewardChoice && Date.parse(row.eligibleAfter) <= now;
}

export function rewardLabel(row, now = Date.now()) {
  if (row.status === 'pending') {
    if (row.rewardChoice === 'credit') return 'Credit being issued';
    return Date.parse(row.eligibleAfter) <= now ? 'Ready: choose credit or cash' : '14-day hold';
  }
  return ({ reserved: 'Checkout in progress', credit_issued: 'Credit ready for your next lifetime purchase', credit_redeemed: 'Credit used',
    credit_expired: 'Credit expired', cash_requested: 'Cash requested', cash_ready: 'Cash approved', cash_paid: 'Cash paid',
    paying: 'Payout in progress', paid: 'Payout recorded', cancelled: 'Cancelled', expired: 'Checkout expired',
    review_same_card: 'Under review', recovery_required: 'Under review' })[row.status] || row.status;
}

/** Admin wording for recovery_required: blocked before any value left vs credit or money already out. */
export function recoveryLabel(row) {
  if (row?.status !== 'recovery_required') return '';
  if (row.payoutBlockedReason === 'dispute_or_refund_before_payout') return 'Blocked before payout: dispute or refund found, no money sent';
  if (row.payoutBlockedReason === 'dispute_or_refund_before_credit') return 'Blocked before credit: dispute or refund found, no credit issued';
  if (row.payoutBlockedReason === 'dispute_or_refund_before_cash_release') {
    return 'Blocked before marking cash paid: do not pay. If you already paid, no clawback, review the referrer';
  }
  if (row.recoveryFrom === 'credit_issued') {
    return row.creditRevokedAt ? 'Purchase reversed: unused credit withdrawn' : 'Purchase reversed: delete the unused credit coupon in Stripe';
  }
  if (row.recoveryFrom === 'credit_redeemed') return 'Reversed after credit was used: no clawback, review the referrer';
  if (row.recoveryFrom === 'cash_ready') return 'Purchase reversed after cash release: do not pay. If you already paid, no clawback, review the referrer';
  if (row.payoutReference) return 'Reversed after payout was sent: no clawback, review the referrer';
  return 'Needs review';
}

const PURCHASED = ['pending', 'paying', 'paid', 'recovery_required', 'review_same_card', 'credit_issued', 'credit_redeemed', 'credit_expired',
  'cash_requested', 'cash_ready', 'cash_paid'];

/** Referrer funnel: clicks → verified sign-ups → Mock 1 finishers → purchases → rewards. */
export function referralStats(data) {
  const rewards = data?.rewards || [];
  const sum = statuses => rewards.filter(r => statuses.includes(r.status)).reduce((t, r) => t + (r.rewardUsd || 0), 0);
  return {
    clicks: data?.visits || 0,
    signups: data?.referredAccounts || 0,
    mocks: data?.completedMocks || 0,
    purchases: rewards.filter(r => PURCHASED.includes(r.status)).length,
    // Delivered: paid out, credit issued or used. Pending: on hold, awaiting a choice or a cash payout.
    earnedUsd: sum(['paid', 'cash_paid', 'credit_issued', 'credit_redeemed']),
    pendingUsd: sum(['pending', 'paying', 'cash_requested', 'cash_ready', 'review_same_card']),
  };
}

/** Shareable link: /r/<username> when chosen, else /r/<code>. */
export function referralLink(origin, data) {
  return `${origin}/r/${data?.handle || data?.code}`;
}

/** Ready-to-paste invite for group chats and forums. Short, plain, no emoji. */
export function inviteMessage(link) {
  return `I'm using CFA Mock Exam for Level 1 prep. Mock 1 is free (90 questions, timed, instant score), and my link gets you 10% off lifetime: ${link}`;
}

/** Share targets for the invite. Every value is URL-encoded; the link appears once. */
export function shareLinks(link, message = inviteMessage(link)) {
  const textOnly = message.replace(` ${link}`, '').replace(link, '').trim();
  return {
    whatsapp: `https://wa.me/?text=${encodeURIComponent(message)}`,
    telegram: `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(textOnly)}`,
  };
}

/**
 * Plain-language reward lines for the card, from the server offer. Free-week copy appears only
 * while that reward is switched on; with no friend reward the card keeps the 10% offer alone.
 */
export function referralRewardCopy(data) {
  const credit = data?.friendCredit;
  const week = data?.freeWeek;
  if (credit) return {
    headline: 'Invite friends, save on lifetime',
    lines: [`Every friend who takes Mock 1 gives you $${credit.perFriendUsd} off lifetime (up to $${credit.capUsd})`,
      ...(week ? [`Plus 1 free week of full access per friend (up to ${week.cap} ${week.cap === 1 ? 'week' : 'weeks'})`] : []),
      'Plus 10% back when they buy'],
  };
  if (week) return {
    headline: 'Invite friends, study free',
    lines: [`1 free week of full access for every friend who takes Mock 1 (up to ${week.cap} ${week.cap === 1 ? 'week' : 'weeks'})`,
      'Plus 10% back when they buy lifetime'],
  };
  return { headline: 'Earn 10% on every lifetime plan your friends buy', lines: [] };
}

/** Mirrors netlify/functions/_quizServer.js TRIAL_KEY_SITTINGS_PER_DAY (display only; the server enforces it). */
export const FREE_WEEK_KEY_SITTINGS_PER_DAY = 2;
